/**
 * DockingWorkspace.jsx
 *
 * 3-panel docking UI — supports both live docking and history view mode.
 *
 * Props (all optional):
 *   initialBlob    {Blob}    — ZIP blob from history API; triggers auto-hydration
 *   initialJobId   {string}  — execution ID to display in header
 *   isHistoryMode  {boolean} — hides the prep left panel, shows results directly
 *
 * Normal flow (isHistoryMode=false, no initialBlob):
 *  1. Upload protein (PDB ID or file) + ligand → call /prepare
 *     → Loader shown → SSE COMPLETED → download ZIP → extract → Molstar
 *  2. User clicks "Detect Pockets" → POST /detect-pockets/{jobId}?padding=N
 *     → Loader shown → SSE COMPLETED → GET /detect-pockets/result/{jobId}
 *  3. User selects pockets → Run Docking
 *     → Loader shown → SSE COMPLETED → GET /run/download/{jobId}
 *     → extract ZIP → Molstar + Scores + Interactions
 *
 * History mode (isHistoryMode=true, initialBlob provided):
 *  - On mount, extracts the initialBlob ZIP immediately
 *  - Loads first PDB pose into Molstar
 *  - Shows Scores + Interactions tabs straight away
 *  - Left sidebar (prep steps) is hidden
 */
import React, { useState, useCallback, useEffect, useRef } from "react";
import Loader from "../../../components/Loader";

import {
  fetchPdbStructure, fetchTextFromUrl,
  submitPrepareJob, getResponseJobId,
  submitPocketDetect, getPocketDetectResult,
  getPocketDownloadUrl,
  submitPocketDocking,
  submitDirectDocking, getDockStatus, getPocketDockStatus,
  getDockResult,
  getDockDownloadUrl, downloadFileFromUrl,
  getInteractionDiagram, getInteractionData,
} from "./Dockingservice";
import QueueNotificationModal from "../../../components/retro/QueueNotificationModal";
import { useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import DockingDownloadResults from "./DockingdownloadResults";

const MOLSTAR_ORIGIN = "https://cascade-454406.web.app";
const MOLSTAR_URL = `${MOLSTAR_ORIGIN}/viewer/index.html?v=20260424-4`;
const DOCKING_BASE = "https://34.160.16.181.nip.io/docking-api-prod-apigee/api/docking";

/* ─── helper: fetch ZIP with retry ─── */
async function fetchZipWithRetry(url, maxAttempts = 20) {
  const token = sessionStorage.getItem("id_token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  for (let a = 1; a <= maxAttempts; a++) {
    console.log(`[FETCH-ZIP] #${a} → ${url}`);
    try {
      const res = await fetch(url, { headers });
      console.log(`[FETCH-ZIP] status=${res.status}`);
      if (!res.ok) { await new Promise(r => setTimeout(r, Math.min(3000 + a * 500, 10000))); continue; }
      const blob = await res.blob();
      if (blob.size < 100) { await new Promise(r => setTimeout(r, Math.min(3000 + a * 500, 10000))); continue; }
      console.log(`[FETCH-ZIP] success size=${blob.size}`);
      return blob;
    } catch (e) {
      console.warn(`[FETCH-ZIP] err: ${e.message}`);
      await new Promise(r => setTimeout(r, Math.min(3000 + a * 500, 10000)));
    }
  }
  throw new Error("ZIP download failed after max attempts");
}

async function extractZipBlob(blob) {
  // ── AUTO-DOWNLOAD the original zip ──────────────────────────────────────
  // const zipUrl = URL.createObjectURL(blob);
  // const a = document.createElement("a");
  // a.href = zipUrl;
  // a.download = "docking_results.zip";
  // document.body.appendChild(a);
  // a.click();
  // document.body.removeChild(a);
  // URL.revokeObjectURL(zipUrl);
  // ────────────────────────────────────────────────────────────────────────

  const JSZipImport = await import("jszip");
  const JSZip = JSZipImport.default || JSZipImport;
  const zip = await JSZip.loadAsync(blob);
  const allNames = Object.keys(zip.files).filter(n => !zip.files[n].dir);
  console.log("[EXTRACT-ZIP] all contents:", allNames);

  const stripPrefix = (name) => {
    const parts = name.split("/");
    return parts.length > 1 ? parts.slice(1).join("/") : name;
  };

  const result = {
    pdbFiles: [],
    sdfText: null,
    jsonData: null,
    pngBlobs: [],
    reportScores: null,  // ← NEW: parsed from .txt
    reportClashes: null, // ← NEW
  };

  for (const name of allNames) {
    const f = zip.files[name];
    const baseName = stripPrefix(name);
    const lo    = baseName.toLowerCase();
    const rawLo = name.toLowerCase();
    if (!lo && !rawLo) continue;

    if (lo.endsWith(".pdb") || lo.endsWith(".pdbqt")) {
      const text = await f.async("string");
      const displayName = baseName.split("/").pop();
      result.pdbFiles.push({ name: displayName, text });

    } else if (lo.endsWith(".sdf") || lo.endsWith(".mol")) {
      result.sdfText = await f.async("string");

    } else if (lo.endsWith(".json")) {
      const raw = await f.async("string");
      try {
        result.jsonData = JSON.parse(raw);

        // Populate reportScores + reportClashes from JSON —
        // same field names as parseDockingReport() so the UI works identically
        const bestResult = result.jsonData.results?.find(
          r => r.pocket_rank === result.jsonData.best_pocket_rank
        ) ?? result.jsonData.results?.[0];

        if (bestResult) {
          result.reportScores = (bestResult.scores ?? []).map(s => ({
            pose:        s.pose,
            affinity:    s.affinity,
            rmsd_lb:     s.rmsd_lb,
            rmsd_ub:     s.rmsd_ub,
            CNNscore:    s.cnn_score    ?? null,
            CNNaffinity: s.cnn_affinity ?? null,
          }));

          result.reportClashes = bestResult.n_clashes ?? null;
        }

        console.log("[EXTRACT-ZIP] JSON report parsed:", {
          scores:  result.reportScores,
          clashes: result.reportClashes,
        });
      } catch (_) {}

    } else if (lo.endsWith(".png")) {
      const ab = await f.async("arraybuffer");
      const displayName = baseName.split("/").pop();
      result.pngBlobs.push({ name: displayName, blob: new Blob([ab], { type: "image/png" }) });

    } else if (lo.endsWith(".txt") || rawLo.endsWith(".txt")) {
      const text = await f.async("string");
      const parsed = parseDockingReport(text);
      result.reportScores = parsed.scores;
      result.reportClashes = parsed.clashes;
      console.log("[EXTRACT-ZIP] TXT report parsed:", parsed);
    }
  }

  result.pdbFiles.sort((a, b) => a.name.localeCompare(b.name));
  result.pngBlobs.sort((a, b) => a.name.localeCompare(b.name));
  return result;
}

/* ═══════════════════════════════════════════
   UTILITIES
═══════════════════════════════════════════ */
function parseChainsFromPdb(pdbText) {
  if (!pdbText) return [];
  const seqresChains = new Set();
  const compndParts = [];
  const atomChains = new Set();
  pdbText.split("\n").forEach((line) => {
    if (line.startsWith("SEQRES")) { const c = line[11]?.trim(); if (c) seqresChains.add(c); return; }
    if (line.startsWith("COMPND")) { compndParts.push(line.slice(10).trim()); return; }
    if (line.startsWith("ATOM")) { const c = line[21]?.trim(); if (c) atomChains.add(c); }
  });
  if (seqresChains.size > 0) return [...seqresChains].sort();
  if (compndParts.length > 0) {
    const txt = compndParts.join(" ");
    const poly = new Set();
    for (const m of txt.matchAll(/CHAIN:\s*([^;]+)/gi))
      m[1].split(",").map(x => x.trim()).filter(Boolean).forEach(c => poly.add(c));
    if (poly.size > 0) return [...poly].sort();
  }
  return [...atomChains].sort();
}

function filterPdbContent(pdbText, { keepChains, removeLigands, removeWater }) {
  if (!pdbText) return pdbText;
  function isWater(line) { const r = line.slice(17, 20).trim().toUpperCase(); return r === "HOH" || r === "WAT" || r === "H2O"; }
  return pdbText.split("\n").filter(line => {
    if (!line.startsWith("ATOM") && !line.startsWith("HETATM")) return true;
    const c = line[21]?.trim();
    if (keepChains?.size && !keepChains.has(c)) return false;
    if (line.startsWith("HETATM")) {
      if (removeWater && isWater(line)) return false;
      if (removeLigands && !isWater(line)) return false;
    }
    return true;
  }).join("\n");
}

function detectFormat(filename = "") {
  const n = filename.toLowerCase();
  if (n.endsWith(".mol2")) return "mol2";
  if (n.endsWith(".sdf") || n.endsWith(".mol")) return "sdf";
  if (n.endsWith(".pdb") || n.endsWith(".pdbqt")) return "pdb";
  if (n.endsWith(".smi") || n.endsWith(".smiles")) return "smiles";
  return "pdb";
}

function inferDownloadName(cd, fb) {
  const u = cd?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (u) return decodeURIComponent(u);
  return cd?.match(/filename="?([^"]+)"?/i)?.[1] || fb;
}

function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

function saveTextAsFile(text, filename, type = "text/plain;charset=utf-8") {
  saveBlob(new Blob([text], { type }), filename);
}

function mergePdbAndLigand(pdbText, ligandText, ligandFormat) {
  if (!pdbText) return ligandText || "";
  if (!ligandText) return pdbText;

  if (ligandFormat === "pdb" || ligandFormat === "pdbqt") {
    const ligLines = ligandText.split("\n")
      .filter(l => l.startsWith("HETATM") || l.startsWith("ATOM") || l.startsWith("CONECT"));
    const base = pdbText.replace(/^END\s*$/m, "").trimEnd();
    return base + "\n" + ligLines.join("\n") + "\nEND\n";
  }

  try {
    const lines = ligandText.split("\n");
    const countsLine = lines[3] || "";
    const nAtoms = parseInt(countsLine.substring(0, 3).trim(), 10) || 0;
    const hetatmLines = [];
    let serial = 9000;
    for (let i = 4; i < 4 + nAtoms && i < lines.length; i++) {
      const l = lines[i];
      if (l.length < 39) continue;
      const x = parseFloat(l.substring(0, 10).trim());
      const y = parseFloat(l.substring(10, 20).trim());
      const z = parseFloat(l.substring(20, 30).trim());
      const elem = l.substring(31, 34).trim() || "C";
      hetatmLines.push(
        "HETATM" + String(serial++).padStart(5) + "  " + elem.padEnd(3) +
        " LIG L   1    " + x.toFixed(3).padStart(8) + y.toFixed(3).padStart(8) +
        z.toFixed(3).padStart(8) + "  1.00  0.00          " + elem.padEnd(2)
      );
    }
    if (hetatmLines.length === 0) return pdbText;
    const base = pdbText.replace(/^END\s*$/m, "").trimEnd();
    return base + "\n" + hetatmLines.join("\n") + "\nEND\n";
  } catch (_) {
    return pdbText;
  }
}

function parseDockingReport(text) {
  const scores = [];
  const clashes = null;
  let clashCount = null;

  const lines = text.split("\n");
  let inScores = false;

  for (const raw of lines) {
    const line = raw.trim();

    if (/^Pose\s*\|\s*Affinity/i.test(line)) { inScores = true; continue; }
    if (inScores && /^-{10,}/.test(line)) { continue; }  // separator line

    if (inScores && line.startsWith("|")) {
      if (/^-{4,}/.test(line) || line === "") { inScores = false; continue; }
    }

    if (inScores && line !== "") {
      const parts = line.split("|").map(s => s.trim());
      if (parts.length >= 3 && !isNaN(Number(parts[0]))) {
        scores.push({
          pose:        parseInt(parts[0], 10),
          affinity:    parseFloat(parts[1]),
          rmsd_lb:     parseFloat(parts[2]),
          rmsd_ub:     parseFloat(parts[3]),
          CNNscore:    parts[4] !== undefined ? parseFloat(parts[4]) : null,
          CNNaffinity: parts[5] !== undefined ? parseFloat(parts[5]) : null,
        });
      }
      if (parts.length < 3 || isNaN(Number(parts[0]))) inScores = false;
    }

    const clashMatch = line.match(/^Clashes:\s*(\d+)/i);
    if (clashMatch) clashCount = parseInt(clashMatch[1], 10);
  }

  return { scores, clashes: clashCount };
}

/* ═══════════════════════════════════════════
   usePoll
═══════════════════════════════════════════ */
function usePoll(fetchFn, onData, active, ms = 2500) {
  const timer = useRef(null);
  const ar = useRef(active), fr = useRef(fetchFn), cr = useRef(onData);
  useEffect(() => { ar.current = active; }, [active]);
  useEffect(() => { fr.current = fetchFn; }, [fetchFn]);
  useEffect(() => { cr.current = onData; }, [onData]);
  useEffect(() => {
    if (!active) { clearTimeout(timer.current); return; }
    let dead = false;
    async function tick() {
      if (dead || !ar.current) return;
      try { const d = await fr.current(); if (!dead && cr.current(d)) return; } catch (_) { }
      if (!dead && ar.current) timer.current = setTimeout(tick, ms);
    }
    tick();
    return () => { dead = true; clearTimeout(timer.current); };
  }, [active, ms]);
}

/* ═══════════════════════════════════════════
   MolstarViewer
═══════════════════════════════════════════ */
const MolstarViewer = React.forwardRef(function MolstarViewer({ structure }, ref) {
  const iframeRef = useRef(null);
  const loadedRef = useRef(false);
  const queueRef = useRef([]);

  function postToViewer(msg) {
    const win = iframeRef.current?.contentWindow;
    if (!win) { console.warn("[MOLSTAR] no contentWindow"); return; }
    try { win.postMessage(msg, MOLSTAR_ORIGIN); } catch (e) { console.error("[MOLSTAR] postMessage failed:", e); }
  }

  function sendMsg(msg) {
    if (!loadedRef.current) { queueRef.current.push(msg); return; }
    postToViewer(msg);
  }

  React.useImperativeHandle(ref, () => ({ send: sendMsg }));

  useEffect(() => {
    loadedRef.current = false;
    queueRef.current = [];
    function handleViewerMessage(event) {
      if (event.origin !== MOLSTAR_ORIGIN) return;
      if (!iframeRef.current?.contentWindow) return;
      if (event.data?.type !== "MOLSTAR_READY") return;
      loadedRef.current = true;
      queueRef.current.forEach(postToViewer);
      queueRef.current = [];
    }
    window.addEventListener("message", handleViewerMessage);
    return () => window.removeEventListener("message", handleViewerMessage);
  }, []);

  useEffect(() => {
    if (!structure?.text) return;
    const fmt = structure.format || "pdb";
    sendMsg({ type: "LOAD_STRUCTURE", data: structure.text, format: fmt });
    if (fmt === "pdb" || fmt === "pdbqt")
      sendMsg({ type: "LOAD_COMPLEX", structure: structure.text, format: fmt });
  }, [structure]);

  return (
    <iframe
      key={MOLSTAR_URL}
      ref={iframeRef}
      src={MOLSTAR_URL}
      title="Molecular Viewer"
      style={{ width: "100%", height: "100%", border: "none", display: "block" }}
      allow="fullscreen"
      allowFullScreen
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
    />
  );
});

/* ═══════════════════════════════════════════
   DESIGN ATOMS
═══════════════════════════════════════════ */
const iStyle = {
  width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 8,
  fontSize: 13, outline: "none", background: "#fff", boxSizing: "border-box", transition: "all 0.2s ease",
};

function Dot({ status }) {
  const bg = { running: "#f59e0b", completed: "#10b981", failed: "#ef4444" }[status] || "#d1d5db";
  return <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: bg, flexShrink: 0, boxShadow: `0 0 8px ${bg}40` }} />;
}

function Bar({ pct, running }) {
  if (!running) return null;
  return (
    <div style={{ height: 3, background: "#ede9fe", borderRadius: 2, overflow: "hidden", marginTop: 8 }}>
      <div style={{ height: "100%", background: "linear-gradient(90deg, #7c3aed 0%, #a78bfa 100%)", borderRadius: 2, transition: "width .6s ease", width: `${Math.min(100, Math.max(4, pct || 0))}%` }} />
    </div>
  );
}

function Label({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>{children}</div>;
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label>{label}</Label>
      {children}
      {hint && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function FInput(props) {
  const [f, setF] = useState(false);
  return (
    <input {...props}
      style={{ ...iStyle, borderColor: f ? "#7c3aed" : "#d1d5db", boxShadow: f ? "0 0 0 3px #f5f3ff" : "none", ...props.style }}
      onFocus={() => setF(true)} onBlur={() => setF(false)} />
  );
}

function FSelect({ value, onChange, children }) {
  const [f, setF] = useState(false);
  return (
    <select value={value} onChange={onChange}
      style={{ ...iStyle, cursor: "pointer", borderColor: f ? "#7c3aed" : "#d1d5db" }}
      onFocus={() => setF(true)} onBlur={() => setF(false)}>
      {children}
    </select>
  );
}

function PBtn({ onClick, disabled, children, style = {} }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ width: "100%", padding: "10px 14px", background: disabled ? "#c4b5fd" : "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", transition: "all 0.2s ease", boxShadow: !disabled ? "0 2px 8px rgba(124, 58, 237, 0.25)" : "none", ...style }}>
      {children}
    </button>
  );
}

function GBtn({ onClick, disabled, children, style = {} }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ padding: "7px 12px", background: "#fff", color: "#6b7280", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, transition: "all 0.2s ease", ...style }}
      onMouseEnter={e => { if (!disabled) { e.currentTarget.style.borderColor = "#7c3aed"; e.currentTarget.style.color = "#7c3aed"; e.currentTarget.style.background = "#f5f3ff"; } }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "#d1d5db"; e.currentTarget.style.color = "#6b7280"; e.currentTarget.style.background = "#fff"; }}>
      {children}
    </button>
  );
}

function EBtn({ onClick, disabled, children }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ padding: "8px 16px", background: disabled ? "#a7f3d0" : "linear-gradient(135deg, #059669 0%, #047857 100%)", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", whiteSpace: "nowrap", transition: "all 0.2s ease", boxShadow: !disabled ? "0 2px 8px rgba(5, 150, 105, 0.25)" : "none" }}>
      {children}
    </button>
  );
}

function StatusLine({ status, message, error, pct }) {
  if (!status) return null;
  const c = { running: "#d97706", completed: "#059669", failed: "#dc2626" };
  return (
    <div style={{ fontSize: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Dot status={status} />
        <span style={{ fontWeight: 700, color: c[status] || "#6b7280", textTransform: "capitalize" }}>{status}</span>
        {message && <span style={{ color: "#9ca3af" }}>· {message}</span>}
      </div>
      <Bar pct={pct} running={status === "running"} />
      {error && <div style={{ color: "#dc2626", fontSize: 11, marginTop: 6, wordBreak: "break-all" }}>{error}</div>}
    </div>
  );
}

function Section({ title, step, status, open, onToggle, children }) {
  const nb = { completed: "#d1fae5", running: "#fef3c7", failed: "#fee2e2" }[status] || "#f3f4f6";
  const nc = { completed: "#059669", running: "#d97706", failed: "#dc2626" }[status] || "#9ca3af";
  return (
    <div style={{ borderBottom: "1px solid #f0f0f5" }}>
      <button onClick={onToggle}
        style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: "none", border: "none", cursor: "pointer", textAlign: "left", transition: "background 0.15s ease" }}
        onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
        onMouseLeave={e => e.currentTarget.style.background = "none"}>
        <span style={{ width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, flexShrink: 0, background: nb, color: nc }}>{step}</span>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: "#1f2937", textTransform: "uppercase", letterSpacing: "0.04em" }}>{title}</span>
        <Dot status={status} />
        <span style={{ fontSize: 11, color: "#d1d5db", marginLeft: 4 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 12 }}>{children}</div>}
    </div>
  );
}

function ScoreTable({ scores = [] }) {
  const f = (v, d = 3) => v !== null && v !== undefined ? Number(v).toFixed(d) : "—";
  return (
    <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid #ede9fe", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "linear-gradient(135deg, #f5f3ff 0%, #faf8ff 100%)" }}>
            {["#", "Affinity", "RMSD↓", "RMSD↑", "CNN", "CNNaff"].map(h => (
              <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, color: "#7c3aed", borderBottom: "2px solid #ede9fe", whiteSpace: "nowrap" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {!scores.length && <tr><td colSpan={6} style={{ padding: "16px 12px", textAlign: "center", color: "#d1d5db", fontSize: 13 }}>No scores yet</td></tr>}
          {scores.map((s, i) => (
            <tr key={i} onMouseEnter={e => e.currentTarget.style.background = "#f5f3ff"} onMouseLeave={e => e.currentTarget.style.background = ""} style={{ borderBottom: "1px solid #ede9fe" }}>
              <td style={{ padding: "8px 12px", fontFamily: "monospace", fontWeight: 700, color: "#6b7280" }}>{s.pose || i + 1}</td>
              <td style={{ padding: "8px 12px", fontFamily: "monospace", fontWeight: 800, color: "#7c3aed" }}>{f(s.affinity ?? s.minimizedAffinity)}</td>
              <td style={{ padding: "8px 12px", fontFamily: "monospace", color: "#9ca3af" }}>{f(s.rmsd_lb, 2)}</td>
              <td style={{ padding: "8px 12px", fontFamily: "monospace", color: "#9ca3af" }}>{f(s.rmsd_ub, 2)}</td>
              <td style={{ padding: "8px 12px", fontFamily: "monospace", color: "#9ca3af" }}>{f(s.CNNscore ?? s.cnn_score, 4)}</td>
              <td style={{ padding: "8px 12px", fontFamily: "monospace", color: "#9ca3af" }}>{f(s.CNNaffinity ?? s.cnn_affinity)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN WORKSPACE
═══════════════════════════════════════════ */
export default function DockingWorkspace({
  // ── History-mode props ──────────────────────────────────────────────────────
  initialBlob = null,   // Blob from project-history docking result API
  initialJobId = "",     // execution ID for display
  isHistoryMode = false,  // when true: hide prep panel, auto-load results
}) {
  const molstarRef = useRef(null);

  const [jobId, setJobId] = useState(initialJobId || "");

  const [structure, setStructure] = useState(null);
  const [preparedPdbText, setPreparedPdbText] = useState(null);
  const [preparedSdfText, setPreparedSdfText] = useState(null);

  const [allChains, setAllChains] = useState([]);
  const [hiddenChains, setHiddenChains] = useState(new Set());
  const [removeLigands, setRemoveLigands] = useState(false);
  const [removeWaterAtoms, setRemoveWaterAtoms] = useState(false);
  const [editedMolstarPdb, setEditedMolstarPdb] = useState(null);
  const [availableChains, setAvailableChains] = useState([]);
  const [refLigandCenter, setRefLigandCenter] = useState(null);

  const [showGrid, setShowGrid] = useState(false);
  const [gridCenter, setGridCenter] = useState(null);
  const [gridBoxSize, setGridBoxSize] = useState(null);
  const location = useLocation();
  const oid = location.pathname.split('/')[3];
  const { projectName, projectDescription, id } = location.state || {};
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const userName = activeAccount?.name;
  console.log(oid, projectName, userName)
  const userProfile = (() => {
    try { return JSON.parse(sessionStorage.getItem("userProfile") || "{}"); }
    catch { return {}; }
  })();
  const { companyId, userId } = userProfile;

  const [open, setOpen] = useState({ prot: !isHistoryMode, lig: false, dock: isHistoryMode });
  const tog = k => setOpen(p => ({ ...p, [k]: !p[k] }));

  const [ss, setSs] = useState({ prot: null, lig: null, dock: isHistoryMode ? "completed" : null });
  const setS = (k, v) => setSs(p => ({ ...p, [k]: v }));

  const [rightTab, setRightTab] = useState(isHistoryMode ? "scores" : "pockets");

  /* ─── PROTEIN ─── */
  const [pdbId, setPdbId] = useState("");
  const [pdbFile, setPdbFile] = useState(null);
  const [pdbIdFile, setPdbIdFile] = useState(null);
  const [pdbLookup, setPdbLookup] = useState({ status: null, message: "" });
  const [keepWater, setKeepWater] = useState(false);
  const [ph, setPh] = useState(7.4);
  const [protRunning, setProtRunning] = useState(false);
  const [protSt, setProtSt] = useState(null);
  const [protResult, setProtResult] = useState(null);

  /* ─── ZIP status ─── */
  const [zipDownloading, setZipDownloading] = useState(false);
  const [zipExtracted, setZipExtracted] = useState(false);

  /* ─── Loader overlay ─── */
  const [loaderVisible, setLoaderVisible] = useState(false);
  const [loaderMsg, setLoaderMsg] = useState("");
  const showLoader = msg => { setLoaderMsg(msg); setLoaderVisible(true); };
  const hideLoader = () => { setLoaderVisible(false); setLoaderMsg(""); };

  /* ─── SSE waiters ─── */
  const sseWaitersRef = useRef(new Map());

  useEffect(() => {
    function onSseEvent(e) {
      const payload = e.detail;
      const taskId = payload?.taskId;
      const status = payload?.status?.toUpperCase();
      if (!taskId) return;
      const waiter = sseWaitersRef.current.get(taskId);
      if (!waiter) return;
      if (status === "COMPLETED" || status === "FAILED") {
        sseWaitersRef.current.delete(taskId);
        waiter.resolve({ status, payload });
      }
    }
    window.addEventListener("sseTaskUpdate", onSseEvent);
    return () => window.removeEventListener("sseTaskUpdate", onSseEvent);
  }, []);

  function waitForSse(taskId) {
    return new Promise(resolve => sseWaitersRef.current.set(taskId, { resolve }));
  }

  useEffect(() => {
    function handleMolstarMessages(event) {
      if (event.origin !== MOLSTAR_ORIGIN) return;
      const msg = event.data;
      if (msg?.type === "EDITED_PDB_EXPORT") {
        setEditedMolstarPdb(msg.pdbText);
      }
      if (msg?.type === "AVAILABLE_CHAINS") {
        setAvailableChains(msg.chains || []);
      }
    }
    window.addEventListener("message", handleMolstarMessages);
    return () => window.removeEventListener("message", handleMolstarMessages);
  }, []);

  /* pocket */
  const [pktPad, setPktPad] = useState(4);
  const [pktRunning, setPktRunning] = useState(false);
  const [pktPolling, setPktPolling] = useState(false);
  const [pktSt, setPktSt] = useState(null);
  const [pockets, setPockets] = useState([]);
  const [selPkts, setSelPkts] = useState(new Set());
  const [hasPockets, setHasPockets] = useState(false);
  const [pocketsDetected, setPocketsDetected] = useState(false);
  const [expandedPocket, setExpandedPocket] = useState(null);

  /* ─── LIGAND ─── */
  const [ligFile, setLigFile] = useState(null);
  const [ligFmt, setLigFmt] = useState("sdf");
  const [nConf, setNConf] = useState(1);
  const [ligRunning, setLigRunning] = useState(false);
  const [ligSt, setLigSt] = useState(null);
  const [ligResult, setLigResult] = useState(null);
  const [exampleLoading, setExampleLoading] = useState(false);

  /* ─── DOCKING ─── */
  const [gridSrc, setGridSrc] = useState("pocket");
  const [cx, setCx] = useState(""), [cy, setCy] = useState(""), [cz, setCz] = useState("");
  const [sx, setSx] = useState(22), [sy, setSy] = useState(22), [sz, setSz] = useState(22);
  const [refSx, setRefSx] = useState(22), [refSy, setRefSy] = useState(22), [refSz, setRefSz] = useState(22);
  const [blindPad, setBlindPad] = useState(4), [manPad, setManPad] = useState(4), [refPad, setRefPad] = useState(4);
  const [exhaust, setExhaust] = useState(8);
  const [nPoses, setNPoses] = useState(5);
  const [dockRunning, setDockRunning] = useState(false);
  const [dockSt, setDockSt] = useState(null);
  const [isPktDock, setIsPktDock] = useState(false);
  const [queueModal, setQueueModal] = useState({ open: false, jobId: "" });


  /* ─── RESULTS ─── */
  const [dockResult, setDockResult] = useState(null);
  const [ixnImg, setIxnImg] = useState(null);
  const [ixnSummary, setIxnSummary] = useState(null);
  const [ixnLoading, setIxnLoading] = useState(false);
  const [ixnRank, setIxnRank] = useState(0);
  const [downloadBusy, setDownloadBusy] = useState("");

  const [poseFiles, setPoseFiles] = useState([]);
  const [activePose, setActivePose] = useState(null);
  const [ixnImages, setIxnImages] = useState([]);
  const [ixnPopup, setIxnPopup] = useState(null);
  const [
    selectedChain,
    setSelectedChain
  ] = useState(null);

  /* ─── History mode: hydration loading state ─── */
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [showDownloadPopup, setShowDownloadPopup] = useState(false);


  const protFileInputRef = useRef(null);
  const ligFileInputRef = useRef(null);
  const [molstarKey, setMolstarKey] = useState(0);
  const navigate = useNavigate();

  /* ═══════════════════════════════════════════
     HISTORY MODE — auto-hydrate from initialBlob
     Runs once on mount when isHistoryMode=true and initialBlob is provided.
     Extracts ZIP → loads poses, interaction PNGs, scores → sends first PDB
     to Molstar → opens right panel at Scores tab.
  ═══════════════════════════════════════════ */
  useEffect(() => {
    if (!isHistoryMode || !initialBlob) return;

    async function hydrateFromBlob() {
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const { pdbFiles, sdfText, jsonData, pngBlobs, reportScores, reportClashes }
          = await extractZipBlob(initialBlob);
        // saveBlob(initialBlob, `${initialJobId || "docking-result"}.zip`);


        setPoseFiles(pdbFiles);

        if (pdbFiles.length > 0) {
          setActivePose(pdbFiles[0].name);
          const combined = sdfText
            ? mergePdbAndLigand(pdbFiles[0].text, sdfText, "sdf")
            : pdbFiles[0].text;
          setStructure({ text: combined, format: "pdb" });
          molstarRef.current?.send({ type: "LOAD_STRUCTURE", data: combined, format: "pdb" });
          molstarRef.current?.send({ type: "LOAD_COMPLEX", structure: combined, format: "pdb" });
        }

        const ixnUrls = pngBlobs.map(({ name, blob: b }) => ({
          name,
          url: URL.createObjectURL(b),
        }));
        setIxnImages(ixnUrls);
        if (ixnUrls.length > 0) setIxnImg(ixnUrls[0].url);

        // Prefer report scores (from .txt) over JSON, fall back to JSON
        const scores = (reportScores && reportScores.length > 0)
          ? reportScores
          : (jsonData?.scores ?? []);

        const finalResult = {
          n_poses: pdbFiles.length,
          scores,
          n_clashes: reportClashes ?? jsonData?.n_clashes ?? null,
          box_source: jsonData?.box_source ?? null,
          ...(jsonData || {}),
          // Override scores and clashes with report-parsed values
          scores,
          n_clashes: reportClashes ?? jsonData?.n_clashes ?? null,
        };
        setDockResult(finalResult);

        setS("dock", "completed");
        setDockSt({ status: "completed", message: "Loaded from history." });
        setRightTab("scores");

      } catch (e) {
        console.error("[HISTORY] hydration error:", e);
        setHistoryError(e.message || "Failed to load result.");
      } finally {
        setHistoryLoading(false);
      }
    }

    hydrateFromBlob();
  }, []); // intentionally run once on mount

  /* ═══ PDB utils ═══ */
  function buildEffectivePdb({
    nextHiddenChains = hiddenChains,
    nextRemoveLigands = removeLigands,
    nextRemoveWaterAtoms = removeWaterAtoms
  } = {}) {
    const sourcePdb = editedMolstarPdb || preparedPdbText;
    if (!sourcePdb) return null;
    const visible = new Set(allChains.filter(c => !nextHiddenChains.has(c)));
    return filterPdbContent(sourcePdb, {
      keepChains: nextHiddenChains.size > 0 ? visible : null,
      removeLigands: nextRemoveLigands,
      removeWater: nextRemoveWaterAtoms,
    });
  }

  function syncProteinPreview(nextState = {}) {
    const nextText = buildEffectivePdb(nextState);
    if (!nextText) return;
    setStructure({ text: nextText, format: "pdb" });
  }

  function toggleChain(ch) {
    setHiddenChains(prev => {
      const n = new Set(prev);
      const isRemoving = !n.has(ch);
      if (isRemoving) {
        n.add(ch);
        molstarRef.current?.send({ type: "DELETE_CHAINS", chains: [ch] });
      } else {
        n.delete(ch);
        molstarRef.current?.send({ type: "SHOW_CHAINS", chains: [ch] });
      }
      return n;
    });
  }

  function toggleLigandVisibility() {
    setRemoveLigands(prev => { const next = !prev; syncProteinPreview({ nextRemoveLigands: next }); return next; });
  }

  function toggleWaterVisibility() {
    setRemoveWaterAtoms(prev => { const next = !prev; syncProteinPreview({ nextRemoveWaterAtoms: next }); return next; });
  }

  function resetProteinEdits() {
    setHiddenChains(new Set());
    setRemoveLigands(false);
    setRemoveWaterAtoms(false);
    if (preparedPdbText) setStructure({ text: preparedPdbText, format: "pdb" });
  }

  function toggleGridBox(forceShow) {
    const next = forceShow !== undefined ? forceShow : !showGrid;
    setShowGrid(next);
    molstarRef.current?.send({ type: "SET_GRID_BOX", show: next, center: gridCenter, size: gridBoxSize });
  }



  const downloadEditedPdb = async () => {
  };

  useEffect(() => {
    let nextCenter = null;
    let nextSize = null;
    let nextShow = false;

    if (gridSrc === "pocket" && selPkts.size > 0) {
      const firstRank = [...selPkts].sort((a, b) => a - b)[0];
      const p = pockets.find(x => x.rank === firstRank);
      if (p) {
        nextCenter = { x: p.center_x, y: p.center_y, z: p.center_z };
        nextSize = { x: p.size_x, y: p.size_y, z: p.size_z };
        nextShow = true;
      }
    } else if (gridSrc === "reference_ligand") {
      if (refLigandCenter) {
        nextCenter = refLigandCenter;
        nextSize = { x: refSx, y: refSy, z: refSz };
        nextShow = true;
      }
    } else if (gridSrc === "manual") {
      const numCx = parseFloat(cx), numCy = parseFloat(cy), numCz = parseFloat(cz);
      if (!isNaN(numCx) && !isNaN(numCy) && !isNaN(numCz) &&
        String(cx).trim() !== "" && String(cy).trim() !== "" && String(cz).trim() !== "") {
        nextCenter = { x: numCx, y: numCy, z: numCz };
        nextSize = { x: sx, y: sy, z: sz };
        nextShow = true;
      }
    }
    // blind → nextShow stays false

    setGridCenter(nextCenter);
    setGridBoxSize(nextSize);
    setShowGrid(nextShow);

    molstarRef.current?.send({
      type: "SET_GRID_BOX",
      show: nextShow,
      center: nextCenter,
      size: nextSize,
    });
  }, [gridSrc, selPkts, pockets, refLigandCenter, refSx, refSy, refSz, cx, cy, cz, sx, sy, sz]);

  // Keep grid visible after structure reloads into Molstar
  useEffect(() => {
    if (!showGrid || !gridCenter || !gridBoxSize) return;
    molstarRef.current?.send({ type: "SET_GRID_BOX", show: true, center: gridCenter, size: gridBoxSize });
  }, [structure]);



  function getEffectivePdb() { return buildEffectivePdb(); }

  function loadProteinText(txt, sourceFile = null) {
    setPreparedPdbText(txt);
    setAllChains(parseChainsFromPdb(txt));
    setHiddenChains(new Set());
    setRemoveLigands(false);
    setRemoveWaterAtoms(false);
    setStructure({ text: txt, format: "pdb" });
    if (sourceFile) setPdbIdFile(sourceFile);
  }

  const activeProteinFile = pdbId.trim() ? pdbIdFile : pdbFile;

  useEffect(() => {
    if (isHistoryMode) return; // skip PDB lookup in history mode
    const id = pdbId.trim().toUpperCase();
    if (!id) { setPdbIdFile(null); setPdbLookup({ status: null, message: "" }); return; }
    setPdbFile(null);
    if (id.length !== 4) { setPdbIdFile(null); setPdbLookup({ status: "running", message: "Enter a 4-character PDB ID." }); return; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPdbLookup({ status: "running", message: `Fetching ${id} from RCSB...` });
      try {
        const info = await fetchPdbStructure(id);
        const pdbText = await fetchTextFromUrl(info.pdb_file_url);
        if (cancelled) return;
        const file = new File([pdbText], `${info.pdb_id || id}.pdb`, { type: "chemical/x-pdb" });
        loadProteinText(pdbText, file);
        setPdbLookup({ status: "completed", message: `${info.pdb_id || id} loaded.` });
      } catch (e) {
        if (!cancelled) { setPdbIdFile(null); setPdbLookup({ status: "failed", message: e.message }); }
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [pdbId, isHistoryMode]);

  async function requestEditedPdbFromMolstar() {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Molstar export timeout"));
      }, 10000);

      function handler(event) {
        if (event.origin !== MOLSTAR_ORIGIN) return;
        const msg = event.data;
        if (msg?.type === "EDITED_PDB_EXPORT") {
          clearTimeout(timeout);
          window.removeEventListener("message", handler);
          resolve(msg.pdbText);
        }
        if (msg?.type === "EDITED_PDB_EXPORT_ERROR") {
          clearTimeout(timeout);
          window.removeEventListener("message", handler);
          reject(msg.error);
        }
      }
      window.addEventListener("message", handler);
      molstarRef.current?.send({ type: "EXPORT_EDITED_PDB" });
    });
  }

  /* ═══ PROTEIN + LIGAND PREP ═══ */
  async function prepareProteinAndLigand() {
    if (!activeProteinFile) { alert("Enter a valid PDB ID or upload a protein file."); return; }
    if (!ligFile) { alert("Upload a ligand file."); return; }

    setProtRunning(true); setS("prot", "running");
    setProtSt({ status: "running", progress_pct: 0, message: "Submitting protein…" });
    setProtResult(null);
    setLigRunning(false); setS("lig", "running");
    setLigSt({ status: "running", progress_pct: 0, message: "Waiting for protein prep…" });
    setLigResult(null);
    setDockResult(null); setDockSt(null);
    setIxnImg(null); setIxnSummary(null);
    setPockets([]); setSelPkts(new Set());
    setHasPockets(false); setPocketsDetected(false);
    setZipExtracted(false);
    setRefLigandCenter(null);
    showLoader("Preparing protein & ligand…");



    try {
      if (!pdbId.trim() && pdbFile) {
        const reader = new FileReader();
        reader.onload = e => loadProteinText(e.target.result);
        reader.readAsText(pdbFile);
      }

      // Only export edited PDB from Molstar if there are actual edits
      const hasProteinEdits = hiddenChains.size > 0 || removeLigands || removeWaterAtoms;
      let proteinFileToSend = activeProteinFile;

      if (hasProteinEdits) {
        try {
          const exportedPdb = await requestEditedPdbFromMolstar();
          if (exportedPdb && exportedPdb.trim().length > 100) {
            proteinFileToSend = new File(
              [exportedPdb],
              "edited_protein.pdb",
              { type: "chemical/x-pdb" }
            );
          }
        } catch (e) {
          console.warn("[PREPARE] Molstar export failed, using original:", e);
        }
      }

      const r = await submitPrepareJob({
        pdbFile: proteinFileToSend,
        ligandFile: ligFile,
        keepWater,
        projectId: oid,
        projectName,
        userName,
        userId,
        ph,
        nConformers: nConf,
      });

      const id = getResponseJobId(r);
      if (!id) throw new Error("No job_id returned.");
      setJobId(id);

      const ssePromise = waitForSse(id);
      setLoaderMsg("Preparing…");

      const { status } = await ssePromise;
      if (status === "FAILED") throw new Error("Preparation failed (SSE)");

      setLoaderMsg("Downloading prepared files…");
      setZipDownloading(true);

      const blob = await fetchZipWithRetry(`${DOCKING_BASE}/prepare/download/${encodeURIComponent(id)}`);
      saveBlob(blob, `${id}-prepared.zip`);

      const { pdbFiles, sdfText, jsonData: prepJsonData } = await extractZipBlob(blob);
      const pdbText = pdbFiles[0]?.text || null;

      // Parse ref_ligand_center from preparation result JSON
      const rawCenter = prepJsonData?.protein?.ref_ligand_center;
      if (Array.isArray(rawCenter) && rawCenter.length === 3) {
        const [rx, ry, rz] = rawCenter.map(Number);
        setRefLigandCenter({ x: rx, y: ry, z: rz });
      } else {
        console.warn("[PREP] ref_ligand_center not found in preparation_result.json");
      }

      if (pdbText) {
        setPreparedPdbText(pdbText);
        setAllChains(parseChainsFromPdb(pdbText));
        setHiddenChains(new Set());
        setRemoveLigands(false);
        setRemoveWaterAtoms(false);
      }
      if (sdfText) setPreparedSdfText(sdfText);

      const combined = pdbText
        ? (sdfText ? mergePdbAndLigand(pdbText, sdfText, "sdf") : pdbText)
        : null;
      if (combined) {
        setStructure({ text: combined, format: "pdb" });
        molstarRef.current?.send({ type: "LOAD_STRUCTURE", data: combined, format: "pdb" });
        molstarRef.current?.send({ type: "LOAD_COMPLEX", structure: combined, format: "pdb" });
      }

      setZipDownloading(false);
      setZipExtracted(true);

      setProtResult(r);
      setLigResult(r);
      setS("prot", "completed");
      setS("lig", "completed");
      setProtSt({ status: "completed", message: "Protein & ligand prepared." });
      setLigSt({ status: "completed", message: "Ligand prepared." });
      setOpen(p => ({ ...p, dock: true }));
      setRightTab("pockets");

    } catch (e) {
      console.error("[PREPARE] error:", e);
      setProtSt({ status: "failed", message: e.message }); setS("prot", "failed");
      setLigSt({ status: "failed", message: e.message }); setS("lig", "failed");
      setZipDownloading(false);
    } finally {
      setProtRunning(false);
      setLigRunning(false);
      hideLoader();
    }
  }

  function onProtFile(e) {
    const f = e.target.files[0] || null;
    setPdbId(""); setPdbIdFile(null); setPdbLookup({ status: null, message: "" });
    setPdbFile(f);
    if (f) {
      const reader = new FileReader();
      reader.onload = ev => loadProteinText(ev.target.result);
      reader.readAsText(f);
    }
  }

  async function loadExampleDockingFiles() {
    setExampleLoading(true);
    try {
      const [pdbResp, ligResp] = await Promise.all([fetch("/docking/3PY0.pdb"), fetch("/docking/Bacosine.sdf")]);
      if (!pdbResp.ok || !ligResp.ok) throw new Error("Could not load example files.");
      const [pdbText, ligText] = await Promise.all([pdbResp.text(), ligResp.text()]);
      setPdbId("");
      setPdbFile(new File([pdbText], "3PY0.pdb", { type: "chemical/x-pdb" }));
      setLigFile(new File([ligText], "Bacosine.sdf", { type: "chemical/x-sdf" }));
      setLigFmt("sdf");
      loadProteinText(pdbText);
      setPockets([]); setSelPkts(new Set()); setHasPockets(false); setPocketsDetected(false);
      setProtResult(null); setLigResult(null); setDockResult(null); setDockSt(null);
      setIxnImg(null); setIxnSummary(null);
      setSs({ prot: null, lig: null, dock: null });
      setRightTab("pockets");
      setOpen({ prot: true, lig: true, dock: false });
    } catch (e) {
      alert("Unable to load example files. " + e.message);
    } finally {
      setExampleLoading(false);
    }
  }

  /* ─── POCKET DETECT ─── */
  function normalizePocketList(data) {
    const list = data?.pockets || data?.result?.pockets || data?.data?.pockets || data?.results || data;
    return Array.isArray(list) ? list : [];
  }

  async function detectPockets() {
    setPktRunning(true); setPktPolling(false);
    setHasPockets(false); setPockets([]); setPocketsDetected(false);
    setPktSt({ status: "running", progress_pct: 0, message: "Running fpocket…" });
    showLoader("Detecting binding pockets…");

    try {
      const ssePromise = waitForSse(jobId);
      const result = await submitPocketDetect(jobId, pktPad);
      const list = normalizePocketList(result);
      if (list.length > 0) {
        sseWaitersRef.current.delete(jobId);
        setPockets(list);
        setHasPockets(true);
        setPocketsDetected(true);
        setGridSrc("pocket");
        setPktSt({ status: "completed", message: `${list.length} pocket${list.length === 1 ? "" : "s"} found` });
        setRightTab("pockets");
        setPktRunning(false);
        return;
      }

      setLoaderMsg("Detecting pockets… waiting for server…");
      const { status } = await ssePromise;
      if (status === "FAILED") throw new Error("Pocket detection failed (SSE)");

      setLoaderMsg("Loading pocket results…");
      await new Promise(r => setTimeout(r, 8000));
      const d = await getPocketDetectResult(jobId);
      const finalList = d?.pockets || d?.result?.pockets || [];
      setPockets(finalList);
      setHasPockets(finalList.length > 0);
      setPocketsDetected(true);
      setGridSrc("pocket");
      setPktSt({ status: "completed", message: `${finalList.length} pocket${finalList.length === 1 ? "" : "s"} found` });
      setRightTab("pockets");

    } catch (e) {
      console.error("[POCKETS] error:", e);
      setPktSt({ status: "failed", message: e.message });
    } finally {
      setPktRunning(false);
      hideLoader();
    }
  }

  function togglePkt(rank) {
    setSelPkts(prev => { const n = new Set(prev); n.has(rank) ? n.delete(rank) : n.add(rank); return n; });
  }

  /* ═══ LIGAND FILE ═══ */
  function onLigFile(e) {
    const f = e.target.files[0] || null; setLigFile(f);
    if (f) {
      const fmt = detectFormat(f.name);
      setLigFmt(fmt);
      const reader = new FileReader();
      reader.onload = ev => {
        const ligText = ev.target.result;
        if (preparedPdbText) {
          const combined = mergePdbAndLigand(preparedPdbText, ligText, fmt);
          setStructure({ text: combined, format: "pdb" });
          molstarRef.current?.send({ type: "LOAD_STRUCTURE", data: combined, format: "pdb" });
          molstarRef.current?.send({ type: "LOAD_COMPLEX", structure: combined, format: "pdb" });
        } else {
          setStructure({ text: ligText, format: fmt });
          molstarRef.current?.send({ type: "LOAD_STRUCTURE", data: ligText, format: fmt });
        }
      };
      reader.readAsText(f);
    }
  }

  /* ═══ DOCKING ═══ */
  const fetchPktDock = useCallback(() => getPocketDockStatus(jobId), [jobId]);
  const fetchDirectDock = useCallback(() => getDockStatus(jobId), [jobId]);
  const loadResults = useCallback(async () => {
    try { const d = await getDockResult(jobId); if (!d.pending) { setDockResult(d); setRightTab("scores"); } } catch (_) { }
  }, [jobId]);
  const handleDockPoll = useCallback(d => {
    setDockSt(d);
    if (d.status === "completed") { setDockRunning(false); setS("dock", "completed"); loadResults(); return true; }
    if (d.status === "failed") { setDockRunning(false); setS("dock", "failed"); return true; }
    return false;
  }, [loadResults]);
  usePoll(fetchPktDock, handleDockPoll, false);
  usePoll(fetchDirectDock, handleDockPoll, false);

  async function runDocking() {
    setDockRunning(true);
    setS("dock", "running");
    setShowGrid(false);

    const hasEdits =
      hiddenChains.size > 0 ||
      removeLigands ||
      removeWaterAtoms;

    let extraOpts = {};

    if (hasEdits) {
      try {
        const exportedPdb = await requestEditedPdbFromMolstar();

        if (exportedPdb && exportedPdb.trim().length > 100) {
          extraOpts = {
            modifiedProteinPdb: new Blob(
              [exportedPdb],
              { type: "text/plain" }
            ),
          };
        }
      } catch (e) {
        console.warn(
          "[DOCKING] Molstar export failed, proceeding without edited PDB:",
          e
        );

        // Fall back to local filtering
        const effectivePdb = getEffectivePdb();

        if (effectivePdb) {
          extraOpts = {
            modifiedProteinPdb: new Blob(
              [effectivePdb],
              { type: "text/plain" }
            ),
          };
        }
      }
    }

    showLoader("Submitting docking job…");

    setPoseFiles([]);
    setActivePose(null);
    setIxnImages([]);
    setIxnPopup(null);

    try {
      let result;

      if (gridSrc === "pocket") {

        if (!selPkts.size) {
          setDockRunning(false);
          hideLoader();
          return;
        }

        setIsPktDock(true);

        setDockSt({
          status: "running",
          progress_pct: 0,
          message: `Pockets: #${[...selPkts].join(", #")}`,
        });

        result = await submitPocketDocking(
          jobId,
          [...selPkts].sort((a, b) => a - b),
          {
            padding: pktPad,
            exhaustiveness: exhaust,
            nPoses,
            projectName,
            userName,
            userId: userProfile?.userId,
            projectId: oid,
            ...extraOpts,
          }
        );

      } else {

        if (
          gridSrc === "manual" &&
          (!cx || !cy || !cz)
        ) {
          setDockRunning(false);
          hideLoader();
          return;
        }

        setIsPktDock(false);

        setDockSt({
          status: "running",
          progress_pct: 0,
          message: "Submitting…",
        });

        const gp =
          gridSrc === "reference_ligand"
            ? {
              sizeX: refSx,
              sizeY: refSy,
              sizeZ: refSz,
              padding: refPad,
            }
            : gridSrc === "manual"
              ? {
                centerX: cx,
                centerY: cy,
                centerZ: cz,
                sizeX: sx,
                sizeY: sy,
                sizeZ: sz,
                padding: manPad,
              }
              : {
                padding: blindPad,
              };

        result = await submitDirectDocking(
          jobId,
          gridSrc,
          gp,
          {
            exhaustiveness: exhaust,
            nPoses,
            projectName,
            userName,
            projectId: oid,
            ...extraOpts,
          }
        );
      }

      // Extract job_id from response → show queue modal → done
      const runJobId = getResponseJobId(result) || jobId;

      setDockSt({
        status: "running",
        message: `Job queued: ${runJobId}`,
      });

      setQueueModal({
        open: true,
        jobId: runJobId,
      });

    } catch (e) {

      console.error("[DOCKING] error:", e);

      setDockSt({
        status: "failed",
        message: e.message,
      });

      setS("dock", "failed");

    } finally {

      setDockRunning(false);
      hideLoader();
    }
  }
  async function loadIxn(rank = 0) {
    setIxnRank(rank); setIxnLoading(true); setIxnImg(null); setIxnSummary(null); setRightTab("interactions");
    try { const b = await getInteractionDiagram(jobId, "png", rank); setIxnImg(URL.createObjectURL(b)); } catch (_) { }
    try { const d = await getInteractionData(jobId, rank); setIxnSummary(d); } catch (_) { }
    setIxnLoading(false);
  }

  async function handleRemoteDownload(url, fallbackName, busyKey) {
    if (!url) return;
    setDownloadBusy(busyKey);
    try {
      const { blob, contentDisposition } = await downloadFileFromUrl(url);
      saveBlob(blob, inferDownloadName(contentDisposition, fallbackName));
    } catch (e) { console.error("Download failed", e); }
    finally { setDownloadBusy(""); }
  }

  function downloadCurrentProteinPdb() {
    const pdbText = getEffectivePdb();
    if (!pdbText) return;
    const base = pdbFile?.name?.replace(/\.[^.]+$/, "") || pdbId?.trim()?.toUpperCase() || "protein";
    const hasEdits = hiddenChains.size > 0 || removeLigands || removeWaterAtoms;
    saveTextAsFile(pdbText, `${base}${hasEdits ? "-edited" : "-prepared"}.pdb`, "chemical/x-pdb");
  }

  function getViewerTitle() {
    if (!structure?.text) return isHistoryMode ? "Loading result…" : "No structure loaded";
    if (structure.format === "sdf") return ligResult ? "Prepared ligand preview" : "Ligand preview";
    if (structure.format === "mol2") return "Ligand preview";
    if (dockResult) return "Docked complex preview";
    return protResult ? "Prepared protein preview" : "Protein preview";
  }

  function getViewerSubtitle() {
    if (!structure?.text) return isHistoryMode ? "Extracting ZIP, please wait…" : "Upload a protein or ligand to start building the docking setup.";
    if (structure.format === "sdf" || structure.format === "mol2") return ligFile?.name || "Small-molecule coordinates";
    if (hiddenChains.size > 0) return `Edited protein. Hidden chains: ${[...hiddenChains].join(", ")}`;
    if (removeLigands || removeWaterAtoms) return [removeLigands ? "Ligands removed" : null, removeWaterAtoms ? "Waters removed" : null].filter(Boolean).join(" · ");
    return pdbFile?.name || (pdbId ? `PDB ${pdbId}` : (isHistoryMode ? `Job ${jobId.slice(0, 8)}` : "Protein coordinates"));
  }

  /* ═══ STYLE HELPERS ═══ */
  const gbs = (active, disabled = false) => ({
    flex: 1, padding: "8px 6px", fontSize: 12, fontWeight: 700, borderRadius: 8,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.4 : 1,
    border: `1px solid ${active ? "#7c3aed" : "#d1d5db"}`,
    background: active ? "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)" : "#fff",
    color: active ? "#fff" : "#6b7280",
    transition: "all 0.2s ease",
    boxShadow: active ? "0 2px 8px rgba(124, 58, 237, 0.2)" : "none",
  });

  const tbs = active => ({
    flex: 1, padding: "12px 6px", fontSize: 12, fontWeight: 700, textTransform: "uppercase",
    letterSpacing: "0.06em", background: active ? "#f5f3ff" : "transparent",
    color: active ? "#7c3aed" : "#9ca3af", border: "none",
    borderBottom: active ? "2px solid #7c3aed" : "2px solid transparent",
    cursor: "pointer", transition: "all 0.2s ease",
  });

  /* ═══ RENDER ═══ */
  return (
    <div style={{ display: "flex", flexDirection: "column", height: isHistoryMode ? "100%" : "100vh", background: "#f8f8fb", fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif", overflow: "hidden" }}>

      {/* ── Loader overlay ── */}
      {(loaderVisible || historyLoading) && (
        <div className="fixed inset-0 flex items-center justify-center backdrop-blur-sm bg-opacity-50 z-50">
          <Loader />
          {loaderMsg && <div style={{ position: "absolute", bottom: "calc(50% - 80px)", color: "#fff", fontSize: 13, fontWeight: 600, background: "rgba(0,0,0,0.5)", padding: "6px 16px", borderRadius: 20 }}>{loaderMsg}</div>}
        </div>
      )}

      {/* ── History error banner ── */}
      {historyError && (
        <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", color: "#dc2626", padding: "10px 20px", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
          ⚠ {historyError}
        </div>
      )}

      {/* ── Interaction image fullscreen popup ── */}
      {ixnPopup && (
        <div
          onClick={() => setIxnPopup(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.82)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <div onClick={e => e.stopPropagation()} style={{ position: "relative", maxWidth: "90vw", maxHeight: "90vh" }}>
            <img src={ixnPopup.url} alt={ixnPopup.name} style={{ maxWidth: "90vw", maxHeight: "86vh", borderRadius: 12, boxShadow: "0 24px 60px rgba(0,0,0,0.6)", display: "block" }} />
            <div style={{ position: "absolute", bottom: -36, left: 0, right: 0, textAlign: "center", color: "rgba(255,255,255,0.7)", fontSize: 13, fontWeight: 600 }}>{ixnPopup.name}</div>
            <button onClick={() => setIxnPopup(null)}
              style={{ position: "absolute", top: -14, right: -14, width: 30, height: 30, borderRadius: "50%", background: "#fff", border: "none", cursor: "pointer", fontSize: 16, lineHeight: "30px", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.3)" }}>✕</button>
          </div>
        </div>
      )}

      {/* ══ HEADER — only shown in standalone (non-history) mode ══ */}
      {!isHistoryMode && (
        <header style={{ display: "flex", alignItems: "center", gap: 16, padding: "0 24px", height: 56, background: "#fff", borderBottom: "1px solid #e5e7eb", flexShrink: 0, zIndex: 50, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16, fontWeight: 800, boxShadow: "0 2px 8px rgba(124,58,237,0.25)" }}>⬡</div>
            <span style={{ fontSize: 15, fontWeight: 600, color: "#1f2937" }}>Molecular Docking</span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 72 }}>
            {[["prot", "Protein"], ["lig", "Ligand"], ["dock", "Docking"]].map(([k, label]) => (
              <div key={k} style={{ display: "flex", alignItems: "center", gap: 6, borderRadius: 20, background: ss[k] === "completed" ? "#d1fae5" : ss[k] === "running" ? "#fef3c7" : "#f3f4f6" }} className="mt-2 px-3 py-2">
                <Dot status={ss[k]} />
                <span style={{ fontSize: 12, fontWeight: 600, color: ss[k] === "completed" ? "#059669" : ss[k] === "running" ? "#d97706" : "#9ca3af" }}>{label}</span>
              </div>
            ))}
            {zipDownloading && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, background: "#fef3c7" }}>
                <Dot status="running" /><span style={{ fontSize: 12, fontWeight: 600, color: "#d97706" }}>Downloading ZIP…</span>
              </div>
            )}
            {zipExtracted && !zipDownloading && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, background: "#d1fae5" }}>
                <Dot status="completed" /><span style={{ fontSize: 12, fontWeight: 600, color: "#059669" }}>ZIP saved</span>
              </div>
            )}
            <button
              onClick={downloadEditedPdb}
              className="text-[#7c3aed] bg-[#F5F3FF] text-md rounded-[25px] px-3 py-2 mt-2"
            >
              Edited PDB
            </button>
            <div
              style={{
                display: "flex",
                gap: "10px",
                flexWrap: "wrap",
                marginTop: "10px"
              }}
            >

              <div
                className="flex items-center gap-3 text-[#7c3aed] font-semibold"
              >

                <select

                  value={selectedChain || ""}

                  onChange={(e) => {

                    const chain =
                      e.target.value;

                    setSelectedChain(chain);

                    if (!chain)
                      return;

                    console.log(
                      "[REACT] Highlight chain:",
                      chain
                    );

                    molstarRef.current?.send({

                      type: "HIGHLIGHT_CHAIN",

                      chain

                    });

                  }}

                  className="
      border
      rounded-[25px]
      px-3
      py-2
      text-sm
      bg-[#f5f3ff]
    "
                >

                  <option value="">
                    Select Chain
                  </option>

                  {availableChains.map(ch => (

                    <option
                      key={ch}
                      value={ch}
                    >

                      Chain {ch}

                    </option>
                  ))}

                </select>

                {selectedChain && (

                  hiddenChains.has(selectedChain)

                    ? (

                      <button

                        onClick={() => {

                          toggleChain(
                            selectedChain
                          );
                        }}

                        className="
            bg-green-600
            text-white
            px-4
            py-2
            rounded-lg
          "
                      >

                        Restore Chain {selectedChain}

                      </button>

                    )

                    : (

                      <button

                        onClick={() => {

                          toggleChain(
                            selectedChain
                          );
                        }}

                        className="
            bg-red-600
            text-white
            px-4
            py-2
            rounded-lg
          "
                      >

                        Remove Chain {selectedChain}

                      </button>

                    )
                )}

              </div>

              {selectedChain && (

                !hiddenChains.has(selectedChain) && (

                  <button

                    onClick={() => {

                      console.log(
                        "[REACT] Confirm delete:",
                        selectedChain
                      );

                      toggleChain(selectedChain);

                      setSelectedChain(null);

                    }}

                    style={{

                      marginTop: 10,

                      padding: "10px 14px",

                      borderRadius: 8,

                      border: "none",

                      background: "#ef4444",

                      color: "white",

                      fontWeight: 700,

                      cursor: "pointer"
                    }}
                  >

                    Delete Chain {selectedChain}

                  </button>
                )
              )}

            </div>
          </div>

          {/* Manual grid toggle button in header — only show when grid is active */}
          {showGrid && open.dock && (
            <button onClick={() => toggleGridBox()} style={{
              marginLeft: 12, padding: "6px 14px", fontSize: 12, fontWeight: 700,
              background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
              color: "#fff", border: "1px solid #ddd6fe", borderRadius: 20, cursor: "pointer", transition: "all 0.2s ease",
            }}>✕ Hide Grid</button>
          )}

          {jobId && (
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>Job ID</span>
              <button onClick={() => navigator.clipboard?.writeText(jobId)} title={jobId}
                style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", fontSize: 12, background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 20, padding: "5px 14px", color: "#7c3aed", cursor: "pointer", fontWeight: 700 }}>
                {jobId}
              </button>
            </div>
          )}
          <button
            onClick={() => navigate(-1)}
            style={{
              marginLeft: 'auto', padding: '6px 14px', fontSize: 12, fontWeight: 700,
              background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe',
              borderRadius: 20, cursor: 'pointer',
            }}
          >
            ⤢  Exit
          </button>
        </header>
      )}

      {/* ══ 3-PANEL BODY ══ */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* ─── LEFT — hidden in history mode ─── */}
        {!isHistoryMode && (
          <aside style={{ width: 280, flexShrink: 0, background: "#fff", borderRight: "1px solid #f0f0f5", overflowY: "auto", display: "flex", flexDirection: "column" }}>

            {/* STEP 1: PROTEIN + LIGAND PREP */}
            <Section title="Protein & Ligand Prep" step="1" status={ss.prot || ss.lig} open={open.prot} onToggle={() => tog("prot")}>
              <Field label="PDB ID (RCSB)">
                <FInput placeholder="e.g. 3PY0" maxLength={4} value={pdbId} onChange={e => setPdbId(e.target.value.toUpperCase())} />
              </Field>
              <StatusLine status={pdbLookup.status} message={pdbLookup.message} />
              <Field label="Or upload protein .pdb">
                <input
                  ref={protFileInputRef}
                  key={pdbId.trim() ? "pdb-id-source" : "pdb-file-source"}
                  type="file" accept=".pdb" disabled={!!pdbId.trim()} onChange={onProtFile}
                  style={{ ...iStyle, cursor: pdbId.trim() ? "not-allowed" : "pointer", paddingTop: 5, paddingBottom: 5, opacity: pdbId.trim() ? 0.55 : 1 }}
                />
              </Field>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <Field label="Waters">
                  <FSelect value={keepWater ? "true" : "false"} onChange={e => setKeepWater(e.target.value === "true")}>
                    <option value="false">Remove</option>
                    <option value="true">Keep</option>
                  </FSelect>
                </Field>
                <Field label="pH">
                  <FInput type="number" step="0.1" min={4} max={10} value={ph} onChange={e => setPh(parseFloat(e.target.value))} />
                </Field>
              </div>

              <Field label="Ligand file" hint=".sdf · .mol · .mol2 · .pdb · .smi">
                <input
                  ref={ligFileInputRef}
                  type="file"
                  accept=".sdf,.mol,.mol2,.pdb,.smi"
                  onChange={onLigFile}
                  style={{ ...iStyle, cursor: "pointer", paddingTop: 6, paddingBottom: 6 }}
                />
              </Field>
              {ligFile && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#6b7280" }}>
                  <span style={{ background: "#f5f3ff", color: "#7c3aed", border: "1px solid #ddd6fe", borderRadius: 6, padding: "3px 10px", fontFamily: "monospace", fontWeight: 700 }}>{ligFmt.toUpperCase()}</span>
                  <span style={{ color: "#9ca3af" }}>{ligFile.name}</span>
                </div>
              )}
              <Field label="Conformers">
                <FInput type="number" min={1} max={50} value={nConf} onChange={e => setNConf(parseInt(e.target.value) || 1)} />
              </Field>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
                <PBtn onClick={prepareProteinAndLigand} disabled={protRunning || ligRunning || pdbLookup.status === "running"} style={{ flex: 1 }}>
                  {protRunning || ligRunning ? "Preparing…" : "▶ Prepare Protein & Ligand"}
                </PBtn>
                <GBtn onClick={loadExampleDockingFiles} disabled={exampleLoading} style={{ flex: 0, minWidth: 180 }}>
                  {exampleLoading ? "Loading…" : "📁 Load example"}
                </GBtn>
              </div>

              <StatusLine
                status={protSt?.status || ligSt?.status}
                message={protSt?.message || ligSt?.message}
                error={protSt?.error || ligSt?.error}
                pct={Math.max(protSt?.progress_pct || 0, ligSt?.progress_pct || 0)}
              />

              {(zipDownloading || zipExtracted) && (
                <div style={{ background: zipExtracted ? "linear-gradient(135deg,#f0fdf4,#f1fdf9)" : "linear-gradient(135deg,#fef9c3,#fffbeb)", border: `1px solid ${zipExtracted ? "#86efac" : "#fde68a"}`, borderRadius: 10, padding: "10px 14px", fontSize: 12 }}>
                  {zipDownloading && <div style={{ color: "#92400e", fontWeight: 600 }}>⏳ Downloading prepared files…</div>}
                  {zipExtracted && (
                    <>
                      <div style={{ fontWeight: 700, color: "#059669", marginBottom: 6 }}>✓ ZIP downloaded & extracted</div>
                      <div style={{ color: "#6b7280", fontSize: 11 }}>Protein (.pdb) and ligand (.sdf) loaded into viewer</div>
                    </>
                  )}
                </div>
              )}

              {preparedPdbText && allChains.length > 0 && (
                <div style={{ border: "1px solid #ddd6fe", background: "linear-gradient(135deg, #faf8ff 0%, #f5f3ff 100%)", borderRadius: 10, padding: "12px 14px", marginTop: 4 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "#7c3aed", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Protein Edit</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 12 }}>
                    {allChains.map(ch => {
                      const hidden = hiddenChains.has(ch);
                      return (
                        <button key={ch} onClick={() => toggleChain(ch)} style={{
                          padding: "6px 10px", borderRadius: 999,
                          border: `1.5px solid ${hidden ? "#fca5a5" : "#c4b5fd"}`,
                          background: hidden ? "#fef2f2" : "#f5f3ff",
                          color: hidden ? "#dc2626" : "#6d28d9",
                          fontSize: 12, fontWeight: 700, cursor: "pointer",
                        }}>
                          {hidden ? "▼" : "▲"} {ch}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "#4b5563", cursor: "pointer" }}>
                      <input type="checkbox" checked={removeLigands} onChange={toggleLigandVisibility} style={{ accentColor: "#7c3aed", width: 16, height: 16, cursor: "pointer" }} />
                      Remove ligands
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "#4b5563", cursor: "pointer" }}>
                      <input type="checkbox" checked={removeWaterAtoms} onChange={toggleWaterVisibility} style={{ accentColor: "#7c3aed", width: 16, height: 16, cursor: "pointer" }} />
                      Remove waters
                    </label>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>
                      {hiddenChains.size > 0 || removeLigands || removeWaterAtoms ? "✓ Applied to downloads & docking" : "Visibility-only changes"}
                    </div>
                    <GBtn onClick={resetProteinEdits} disabled={hiddenChains.size === 0 && !removeLigands && !removeWaterAtoms}>Reset</GBtn>
                  </div>
                </div>
              )}

              {/* Pocket detection */}
              {protResult && (
                <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "#7c3aed", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Pocket Detection</div>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                    <div style={{ flex: 1 }}>
                      <Field label="Padding (Å)">
                        <FInput type="number" min={0} max={20} step={0.5} value={pktPad} onChange={e => setPktPad(parseFloat(e.target.value))} />
                      </Field>
                    </div>
                    <EBtn onClick={detectPockets} disabled={pktRunning || pktPolling}>
                      {pktRunning || pktPolling ? "Running…" : "🔍 Detect"}
                    </EBtn>
                  </div>
                  <div style={{ marginTop: 8 }}><StatusLine status={pktSt?.status} message={pktSt?.message} pct={pktSt?.progress_pct} /></div>

                  {hasPockets && pockets.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>{pockets.length} pockets found</span>
                        <button onClick={() => setRightTab("pockets")} style={{ fontSize: 12, color: "#7c3aed", fontWeight: 700, background: "none", border: "none", cursor: "pointer" }}>View all →</button>
                      </div>
                      {pockets.slice(0, 4).map(p => (
                        <div key={p.rank} onClick={() => togglePkt(p.rank)} style={{
                          display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", marginBottom: 6,
                          borderRadius: 8, border: `1px solid ${selPkts.has(p.rank) ? "#7c3aed" : "#d1d5db"}`,
                          background: selPkts.has(p.rank) ? "#f5f3ff" : "#fafafa", cursor: "pointer",
                        }}>
                          <input type="checkbox" style={{ accentColor: "#7c3aed", width: 18, height: 18 }} checked={selPkts.has(p.rank)} onChange={() => togglePkt(p.rank)} onClick={e => e.stopPropagation()} />
                          <span style={{ background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)", color: "#fff", borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 800, fontFamily: "monospace" }}>#{p.rank}</span>
                          <span style={{ fontSize: 12, color: "#6b7280", fontFamily: "monospace", fontWeight: 600 }}>{p.score?.toFixed(3)}</span>
                          <span style={{ marginLeft: "auto", fontSize: 11, color: "#9ca3af", fontFamily: "monospace" }}>{(p.volume_A3 || p.volume || 0).toFixed(0)} Å³</span>
                        </div>
                      ))}
                      {pockets.length > 4 && <button onClick={() => setRightTab("pockets")} style={{ fontSize: 12, color: "#7c3aed", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: "4px 0", marginTop: 4 }}>+{pockets.length - 4} more →</button>}
                      {selPkts.size > 0 && <div style={{ marginTop: 8, fontSize: 12, color: "#7c3aed", fontWeight: 700, background: "#f5f3ff", padding: "6px 10px", borderRadius: 6 }}>✓ {selPkts.size} pocket{selPkts.size > 1 ? "s" : ""} selected</div>}
                    </div>
                  )}
                </div>
              )}
            </Section>

            {/* STEP 3: DOCKING */}
            <Section title="Docking" step="3" status={ss.dock} open={open.dock} onToggle={() => tog("dock")}>
              {hiddenChains.size > 0 && (
                <div style={{ background: "linear-gradient(135deg, #fef3c7 0%, #fef9e7 100%)", border: "1px solid #fde68a", borderRadius: 8, padding: "9px 12px", fontSize: 12, color: "#92400e", fontWeight: 600 }}>
                  ⚠ Modified protein: chains {[...hiddenChains].join(", ")} excluded.
                </div>
              )}

              <div>
                <Label>Grid Source</Label>
                {pocketsDetected ? (
                  <div style={{ background: "linear-gradient(135deg, #f5f3ff 0%, #faf8ff 100%)", border: "1px solid #ddd6fe", borderRadius: 8, padding: "10px 14px", fontSize: 12, fontWeight: 700, color: "#7c3aed", display: "flex", alignItems: "center", gap: 8 }}>
                    <span>🔒</span> Pocket mode (locked after detection)
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7 }}>
                    {[["pocket", "Pockets"], ["reference_ligand", "Ref. Ligand"], ["manual", "Manual"], ["blind", "Blind"]].map(([k, l]) => (
                      <button key={k} onClick={() => setGridSrc(k)} style={gbs(gridSrc === k)}>{l}</button>
                    ))}
                  </div>
                )}
              </div>

              {gridSrc === "pocket" && (
                <div style={{ background: "linear-gradient(135deg, #f5f3ff 0%, #faf8ff 100%)", border: "1px solid #ddd6fe", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "#6b7280", fontWeight: 600 }}>
                  {selPkts.size > 0 ? `✓ ${selPkts.size} pocket${selPkts.size > 1 ? "s" : ""} selected: #${[...selPkts].join(", #")}` : hasPockets ? "Select pockets in the panel →" : "Detect pockets first (Step 1)"}
                </div>
              )}
              {!pocketsDetected && gridSrc === "reference_ligand" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {refLigandCenter ? (
                    <div style={{ background: "linear-gradient(135deg, #f0fdf4 0%, #f1fdf9 100%)", border: "1px solid #86efac", borderRadius: 8, padding: "10px 14px", fontSize: 12 }}>
                      <div style={{ fontWeight: 700, color: "#059669", marginBottom: 6 }}>📍 Center from preparation result</div>
                      <div style={{ fontFamily: "monospace", color: "#374151", fontSize: 12 }}>
                        X: {refLigandCenter.x.toFixed(3)} &nbsp; Y: {refLigandCenter.y.toFixed(3)} &nbsp; Z: {refLigandCenter.z.toFixed(3)}
                      </div>
                      <div style={{ color: "#6b7280", fontSize: 11, marginTop: 4 }}>Grid shown automatically in viewer</div>
                    </div>
                  ) : (
                    <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#92400e", fontWeight: 600 }}>
                      ⚠ Prepare protein & ligand first to auto-detect reference ligand center
                    </div>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 7 }}>
                    {[["Size X", refSx, setRefSx], ["Size Y", refSy, setRefSy], ["Size Z", refSz, setRefSz]].map(([l, v, s]) => (
                      <Field key={l} label={l}><FInput type="number" min={5} max={200} value={v} onChange={e => s(parseFloat(e.target.value))} /></Field>
                    ))}
                  </div>
                </div>
              )}
              {!pocketsDetected && gridSrc === "manual" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 7 }}>
                    {[["Cx", cx, setCx], ["Cy", cy, setCy], ["Cz", cz, setCz]].map(([l, v, s]) => (
                      <Field key={l} label={l}><FInput type="number" step="0.1" value={v} onChange={e => s(e.target.value)} /></Field>
                    ))}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 7 }}>
                    {[["Sx", sx, setSx], ["Sy", sy, setSy], ["Sz", sz, setSz]].map(([l, v, s]) => (
                      <Field key={l} label={l}><FInput type="number" min={5} max={200} value={v} onChange={e => s(parseFloat(e.target.value))} /></Field>
                    ))}
                  </div>
                </div>
              )}
              {!pocketsDetected && gridSrc === "blind" && (
                <div style={{ background: "linear-gradient(135deg, #f0fdf4 0%, #f1fdf9 100%)", border: "1px solid #86efac", borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10, fontWeight: 600 }}>Whole-protein box. Use exhaustiveness ≥ 16.</div>
                  <Field label="Padding (Å)"><FInput type="number" min={0} max={20} step={0.5} value={blindPad} onChange={e => setBlindPad(parseFloat(e.target.value))} /></Field>
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
                <Field label="Exhaustiveness"><FInput type="number" min={1} max={64} value={exhaust} onChange={e => setExhaust(parseInt(e.target.value) || 8)} /></Field>
                <Field label="Poses"><FInput type="number" min={1} max={20} value={nPoses} onChange={e => setNPoses(parseInt(e.target.value) || 5)} /></Field>
              </div>

              {(gridCenter || preparedPdbText) && (
                <button onClick={() => toggleGridBox()} style={{
                  padding: "9px 12px", fontSize: 12, fontWeight: 700,
                  background: showGrid ? "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)" : "#f5f3ff",
                  color: showGrid ? "#fff" : "#7c3aed",
                  border: "1px solid #ddd6fe", borderRadius: 8, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 8, justifyContent: "center",
                }}>
                  <span>⬜</span> {showGrid ? "Hide Grid Box" : "Preview Grid Box"}
                </button>
              )}

              <PBtn onClick={runDocking} disabled={dockRunning}>{dockRunning ? "Running…" : "▶ Run Docking"}</PBtn>
              <StatusLine status={dockSt?.status} message={dockSt?.message} error={dockSt?.error} pct={dockSt?.progress_pct} />

              {ss.dock === "completed" && (
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                  <GBtn onClick={() => setRightTab("scores")} style={{ flex: 1 }}>📊 Scores</GBtn>
                  <GBtn onClick={() => loadIxn(0)} style={{ flex: 1 }}>🔬 Interactions</GBtn>
                </div>
              )}
            </Section>
          </aside>
        )}

        {/* ─── CENTER: Molstar ─── */}
        <main style={{ flex: 1, background: "#1a1a24", overflow: "hidden", position: "relative", minWidth: 0 }}>
          <MolstarViewer key={molstarKey} ref={molstarRef} structure={structure} />


          <div style={{ position: "absolute", top: 18, left: 18, right: 18, display: "flex", justifyContent: "space-between", gap: 14, pointerEvents: "none" }}>
            <div style={{ maxWidth: 460, background: "rgba(20, 30, 50, 0.8)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "12px 14px", backdropFilter: "blur(12px)" }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", marginBottom: 5 }}>{getViewerTitle()}</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.8)", lineHeight: 1.6 }}>{getViewerSubtitle()}</div>
            </div>
            {preparedPdbText && !isHistoryMode && (
              <button onClick={downloadCurrentProteinPdb} style={{
                pointerEvents: "auto", padding: "10px 14px", borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.16)", background: "rgba(20, 30, 50, 0.85)",
                color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", backdropFilter: "blur(12px)",
              }}>
                ⬇ Download PDB
              </button>
            )}
          </div>

          {!structure && !showGrid && !historyLoading && (
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none", userSelect: "none" }}>
              <div style={{ fontSize: 80, opacity: 0.08, lineHeight: 1, marginBottom: 18 }}>⬡</div>
              <p style={{ fontSize: 16, color: "rgba(255,255,255,0.2)", fontWeight: 700, margin: 0 }}>
                {isHistoryMode ? "Loading docking result…" : "Molecular Viewer"}
              </p>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.12)", marginTop: 8 }}>
                {isHistoryMode ? "Extracting ZIP file" : "Upload a protein or enter a PDB ID to begin"}
              </p>
            </div>
          )}
        </main>



        {/* ─── RIGHT panel — always visible in history mode, otherwise only when dock section is open ─── */}
        {(isHistoryMode || open.dock) && (
          <aside style={{ width: 360, flexShrink: 0, background: "#fff", borderLeft: "1px solid #f0f0f5", display: "flex", flexDirection: "column", overflow: "hidden" }}>


            <div style={{ display: "flex", borderBottom: "1px solid #f0f0f5", flexShrink: 0 }}>

              {/* In history mode, don't show Pockets tab since there are none */}
              {!isHistoryMode && <button style={tbs(rightTab === "pockets")} onClick={() => setRightTab("pockets")}>Pockets</button>}
              <button style={tbs(rightTab === "scores")} onClick={() => setRightTab("scores")}>Scores</button>
              <button style={tbs(rightTab === "interactions")} onClick={() => setRightTab("interactions")}>Interactions</button>
              <button style={tbs(rightTab === "download")} onClick={() => setShowDownloadPopup(true)}>Download</button>

            </div>

            <div style={{ flex: 1, overflowY: "auto", position: "relative" }}>

              {/* POCKETS TAB — only in non-history mode */}
              {!isHistoryMode && rightTab === "pockets" && (
                <div style={{ padding: 16 }}>
                  {!hasPockets ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 240, textAlign: "center", color: "#d1d5db" }}>
                      <div style={{ fontSize: 40, marginBottom: 14 }}>🔍</div>
                      <p style={{ fontSize: 13, margin: 0, fontWeight: 700 }}>{pktRunning || pktPolling ? "Detecting pockets…" : "No pockets detected"}</p>
                      <p style={{ fontSize: 12, marginTop: 7, color: "#9ca3af" }}>{pktRunning || pktPolling ? "Polling for results…" : "Run Pocket Detection in Step 1"}</p>
                      {(pktRunning || pktPolling) && <Bar pct={40} running />}
                    </div>
                  ) : (
                    <>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                        <span style={{ fontSize: 14, fontWeight: 800, color: "#1f2937" }}>Binding Pockets</span>
                        <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 600 }}>{pockets.length} found · {selPkts.size} selected</span>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 12, padding: "8px 12px", background: "#f9fafb", borderRadius: 8 }}>
                        <input type="checkbox" style={{ accentColor: "#7c3aed", width: 18, height: 18 }}
                          checked={pockets.length > 0 && pockets.every(p => selPkts.has(p.rank))}
                          onChange={e => e.target.checked ? setSelPkts(new Set(pockets.map(p => p.rank))) : setSelPkts(new Set())} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#6b7280" }}>Select all</span>
                        {selPkts.size > 0 && <button onClick={() => setSelPkts(new Set())} style={{ marginLeft: "auto", fontSize: 12, color: "#7c3aed", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Clear</button>}
                      </div>

                      <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid #ede9fe", marginBottom: 14 }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                          <thead>
                            <tr style={{ background: "linear-gradient(135deg, #f5f3ff 0%, #faf8ff 100%)" }}>
                              {["", "#", "Score", "Vol (Å³)", "Center (x,y,z)", "Box", "Residues", ""].map((h, i) => (
                                <th key={i} style={{ padding: "9px 8px", textAlign: "left", fontWeight: 700, color: "#7c3aed", borderBottom: "2px solid #ede9fe", whiteSpace: "nowrap", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {pockets.map(p => (
                              <tr key={p.rank} onClick={() => togglePkt(p.rank)}
                                style={{ borderBottom: "1px solid #f3f4f6", cursor: "pointer", background: selPkts.has(p.rank) ? "#f5f3ff" : "" }}
                                onMouseEnter={e => { if (!selPkts.has(p.rank)) e.currentTarget.style.background = "#faf8ff"; }}
                                onMouseLeave={e => { if (!selPkts.has(p.rank)) e.currentTarget.style.background = ""; }}>
                                <td style={{ padding: "7px 8px" }} onClick={e => e.stopPropagation()}>
                                  <input type="checkbox" style={{ accentColor: "#7c3aed", width: 16, height: 16 }} checked={selPkts.has(p.rank)} onChange={() => togglePkt(p.rank)} />
                                </td>
                                <td style={{ padding: "7px 8px" }}>
                                  <span style={{ background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)", color: "#fff", borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 800, fontFamily: "monospace" }}>#{p.rank}</span>
                                </td>
                                <td style={{ padding: "7px 8px", fontFamily: "monospace", color: "#6b7280", fontWeight: 700 }}>{p.score?.toFixed(3)}</td>
                                <td style={{ padding: "7px 8px", fontFamily: "monospace", color: "#6b7280" }}>{((p.volume_A3 || p.volume) ?? 0).toFixed(0)}</td>
                                <td style={{ padding: "7px 8px", fontFamily: "monospace", color: "#9ca3af", fontSize: 10, whiteSpace: "nowrap" }}>
                                  ({p.center_x?.toFixed(1)}, {p.center_y?.toFixed(1)}, {p.center_z?.toFixed(1)})
                                </td>
                                <td style={{ padding: "7px 8px", fontFamily: "monospace", color: "#9ca3af", fontSize: 10, whiteSpace: "nowrap" }}>
                                  {p.size_x?.toFixed(0)}×{p.size_y?.toFixed(0)}×{p.size_z?.toFixed(0)}
                                </td>
                                <td style={{ padding: "7px 8px" }} onClick={e => e.stopPropagation()}>
                                  {p.residues?.length > 0 ? (
                                    <button onClick={() => setExpandedPocket(p)}
                                      style={{ fontSize: 10, fontWeight: 700, background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 12, padding: "3px 8px", color: "#7c3aed", cursor: "pointer" }}>
                                      {p.residues.length} res
                                    </button>
                                  ) : <span style={{ fontSize: 10, color: "#d1d5db" }}>—</span>}
                                </td>
                                <td style={{ padding: "7px 8px" }} onClick={e => e.stopPropagation()}>
                                  <a href={getPocketDownloadUrl(jobId, p.rank)} download>
                                    <button style={{ fontSize: 10, background: "none", border: "1px solid #d1d5db", borderRadius: 6, padding: "3px 7px", cursor: "pointer", color: "#6b7280" }}>⬇</button>
                                  </a>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {selPkts.size > 0 && (
                        <div style={{ background: "linear-gradient(135deg, #f0fdf4 0%, #f1fdf9 100%)", border: "1px solid #86efac", borderRadius: 8, padding: "9px 12px", fontSize: 12, color: "#059669", fontWeight: 700 }}>
                          ✓ {selPkts.size} pocket{selPkts.size > 1 ? "s" : ""} selected — ready to dock
                        </div>
                      )}

                      {expandedPocket && (
                        <div style={{ position: "absolute", inset: 0, background: "#fff", zIndex: 20, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid #ede9fe", flexShrink: 0 }}>
                            <span style={{ background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)", color: "#fff", borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 800, fontFamily: "monospace" }}>Pocket #{expandedPocket.rank}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>Binding Residues</div>
                              <div style={{ fontSize: 11, color: "#9ca3af" }}>Score: {expandedPocket.score?.toFixed(3)} · Vol: {((expandedPocket.volume_A3 || expandedPocket.volume) ?? 0).toFixed(0)} Å³</div>
                            </div>
                            <button onClick={() => setExpandedPocket(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#9ca3af" }}>✕</button>
                          </div>
                          <div style={{ overflowY: "auto", flex: 1 }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                              <thead style={{ position: "sticky", top: 0 }}>
                                <tr style={{ background: "linear-gradient(135deg, #f5f3ff 0%, #faf8ff 100%)" }}>
                                  {["Chain", "ResNum", "ResName"].map(h => (
                                    <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#7c3aed", borderBottom: "2px solid #ede9fe", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {(expandedPocket.residues || []).map((r, i) => (
                                  <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}
                                    onMouseEnter={e => e.currentTarget.style.background = "#f5f3ff"}
                                    onMouseLeave={e => e.currentTarget.style.background = ""}>
                                    <td style={{ padding: "8px 16px", fontFamily: "monospace", fontWeight: 700, color: "#7c3aed" }}>{r.chain}</td>
                                    <td style={{ padding: "8px 16px", fontFamily: "monospace", color: "#374151" }}>{r.resnum ?? r.resi}</td>
                                    <td style={{ padding: "8px 16px", fontFamily: "monospace", color: "#6b7280" }}>{r.resname}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* SCORES TAB */}
              {rightTab === "scores" && (
                <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 16 }}>
                  {(!dockResult && poseFiles.length === 0) ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 240, textAlign: "center", color: "#d1d5db" }}>
                      <div style={{ fontSize: 40, marginBottom: 14 }}>📊</div>
                      <p style={{ fontSize: 13, margin: 0, fontWeight: 700 }}>
                        {historyLoading ? "Loading history result…" : ss.dock === "running" ? "Docking in progress…" : "No results yet"}
                      </p>
                      <p style={{ fontSize: 12, marginTop: 7, color: "#9ca3af" }}>
                        {historyLoading ? "Extracting ZIP…" : "Run docking to see scores"}
                      </p>
                      {historyLoading && <Bar pct={60} running />}
                    </div>
                  ) : (
                    <>
                      {/* Best affinity */}
                      {/* {dockResult?.best_affinity !== null && (
                        <div style={{ background: "linear-gradient(135deg, #f5f3ff 0%, #faf8ff 100%)", border: "1px solid #ede9fe", borderRadius: 14, padding: 18, textAlign: "center" }}>
                          <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 6, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>Best Affinity</div>
                          <div style={{ fontSize: 36, fontWeight: 900, color: "#7c3aed", fontFamily: "monospace", lineHeight: 1 }}>
                            {Number(dockResult.best_affinity).toFixed(2)}
                          </div>
                          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 6 }}>kcal/mol</div>
                        </div>
                      )} */}

                      {/* Pose file list */}
                      {poseFiles.length > 0 && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 800, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
                            Docked Poses ({poseFiles.length})
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {poseFiles.map((pf, i) => (
                              <button key={pf.name} onClick={() => {
                                setActivePose(pf.name);
                                setStructure({ text: pf.text, format: "pdb" });
                                molstarRef.current?.send({ type: "LOAD_STRUCTURE", data: pf.text, format: "pdb" });
                                molstarRef.current?.send({ type: "LOAD_COMPLEX", structure: pf.text, format: "pdb" });
                              }}
                                style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${activePose === pf.name ? "#7c3aed" : "#e5e7eb"}`, background: activePose === pf.name ? "#f5f3ff" : "#fafafa", cursor: "pointer", textAlign: "left", transition: "all 0.15s ease" }}>
                                <span style={{ width: 26, height: 26, borderRadius: "50%", background: activePose === pf.name ? "linear-gradient(135deg,#7c3aed,#6d28d9)" : "#e5e7eb", color: activePose === pf.name ? "#fff" : "#6b7280", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, flexShrink: 0 }}>{i + 1}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: activePose === pf.name ? "#7c3aed" : "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pf.name}</div>
                                  {dockResult?.scores?.[i] && (
                                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>
                                      Affinity: <b style={{ color: "#7c3aed" }}>{Number(dockResult.scores[i].affinity ?? dockResult.scores[i].minimizedAffinity).toFixed(3)}</b> kcal/mol
                                    </div>
                                  )}
                                </div>
                                {activePose === pf.name && <span style={{ fontSize: 10, color: "#7c3aed", fontWeight: 700, flexShrink: 0 }}>▶ Viewing</span>}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Stats grid + score table */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 9 }}>
                        {[
                          ["Poses", poseFiles.length || dockResult?.n_poses || 0],
                          ["Clashes", dockResult?.n_clashes !== null ? dockResult.n_clashes : "—"],
                          ["Source", dockResult?.box_source || "—"],
                        ].map(([l, v]) => (
                          <div key={l} style={{ background: "#f9fafb", borderRadius: 10, padding: "10px 8px", textAlign: "center", border: "1px solid #ede9fe" }}>
                            <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4, fontWeight: 700 }}>{l}</div>
                            <div style={{ fontSize: 16, fontWeight: 800, color: "#374151" }}>{v}</div>
                          </div>
                        ))}
                      </div>
                      {dockResult?.scores?.length > 0 && <ScoreTable scores={dockResult.scores} />}

                      {/* Downloads — hide in history mode since files are already local */}
                      {!isHistoryMode && (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 800, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Downloads</div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                            {dockResult?.files_available?.complex_sdf && (
                              <GBtn onClick={() => handleRemoteDownload(getDockDownloadUrl(jobId, "complex_sdf"), "docked-complex.sdf", "dock-sdf")} disabled={downloadBusy === "dock-sdf"}>
                                {downloadBusy === "dock-sdf" ? "Downloading..." : "⬇ SDF"}
                              </GBtn>
                            )}
                            {dockResult?.files_available?.complex_pdb && (
                              <GBtn onClick={() => handleRemoteDownload(getDockDownloadUrl(jobId, "complex_pdb"), "docked-complex.pdb", "dock-pdb")} disabled={downloadBusy === "dock-pdb"}>
                                {downloadBusy === "dock-pdb" ? "Downloading..." : "⬇ PDB"}
                              </GBtn>
                            )}
                          </div>
                        </div>
                      )}

                      {ixnImages.length > 0 && (
                        <GBtn onClick={() => setRightTab("interactions")} style={{ width: "100%", padding: "10px 14px", fontSize: 12 }}>🔬 View Interactions ({ixnImages.length}) →</GBtn>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* INTERACTIONS TAB */}
              {rightTab === "interactions" && (
                <div style={{ padding: 16 }}>
                  {ixnImages.length === 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 240, textAlign: "center", color: "#d1d5db" }}>
                      <div style={{ fontSize: 40, marginBottom: 12 }}>🔬</div>
                      <p style={{ fontSize: 13, margin: 0, fontWeight: 700 }}>
                        {historyLoading ? "Loading interactions…" : ss.dock === "running" ? "Docking in progress…" : "No interactions yet"}
                      </p>
                      <p style={{ fontSize: 12, marginTop: 7, color: "#9ca3af" }}>
                        {historyLoading ? "Extracting ZIP…" : "Complete docking to see interaction diagrams"}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 800, color: "#1f2937", marginBottom: 4 }}>Interaction Diagrams</div>
                      <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 14 }}>Click any image to enlarge</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        {ixnImages.map(({ name, url }, i) => (
                          <div key={name} onClick={() => setIxnPopup({ url, name })}
                            style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #ede9fe", cursor: "pointer" }}
                            onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(124,58,237,0.18)"}
                            onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                            <img src={url} alt={name} style={{ width: "100%", display: "block" }} />
                            <div style={{ padding: "6px 10px", fontSize: 10, fontWeight: 600, color: "#6b7280", background: "#fafafa", borderTop: "1px solid #f0f0f5" }}>
                              Pose {i + 1}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </aside>
        )}

        {showDownloadPopup && (
          <DockingDownloadResults
            poseFiles={poseFiles}
            ixnImages={ixnImages}
            scores={dockResult?.scores ?? []}
            jobId={jobId}
            onClose={() => setShowDownloadPopup(false)}
          />
        )}

        {queueModal.open && (
          <QueueNotificationModal
            taskId={queueModal.jobId}
            workflowType="docking"
            onClose={() => {
              // Clear file input DOM values
              if (protFileInputRef.current) protFileInputRef.current.value = "";
              if (ligFileInputRef.current) ligFileInputRef.current.value = "";

              // Force Molstar iframe remount = guaranteed clean viewer
              setMolstarKey(k => k + 1);

              // Reset everything back to initial state
              setQueueModal({ open: false, jobId: "" });
              setJobId("");
              setPdbId("");
              setPdbFile(null);
              setPdbIdFile(null);
              setPdbLookup({ status: null, message: "" });
              setLigFile(null);
              setLigFmt("sdf");
              setNConf(1);
              setProtRunning(false);
              setProtSt(null);
              setProtResult(null);
              setLigRunning(false);
              setLigSt(null);
              setLigResult(null);
              setPreparedPdbText(null);
              setPreparedSdfText(null);
              setAllChains([]);
              setHiddenChains(new Set());
              setRemoveLigands(false);
              setRemoveWaterAtoms(false);
              setZipDownloading(false);
              setZipExtracted(false);
              setPockets([]);
              setSelPkts(new Set());
              setHasPockets(false);
              setPocketsDetected(false);
              setPktSt(null);
              setExpandedPocket(null);
              setDockSt(null);
              setDockResult(null);
              setDockRunning(false);
              setIxnImg(null);
              setIxnSummary(null);
              setIxnImages([]);
              setIxnPopup(null);
              setPoseFiles([]);
              setActivePose(null);
              setStructure(null);
              setShowGrid(false);
              setGridCenter(null);
              setGridBoxSize(null);
              setGridSrc("pocket");
              setRefLigandCenter(null);
              setEditedMolstarPdb(null);
              setAvailableChains([]);
              setSs({ prot: null, lig: null, dock: null });
              setOpen({ prot: true, lig: false, dock: false });
              setRightTab("pockets");
            }}
            onHistory={() => {
              setQueueModal({ open: false, jobId: "" });
              navigate(-1);
            }}
          />
        )}

      </div>
    </div>
  );
}